import React from 'react';
import { X, CheckCircle, XCircle, Printer, Download } from 'lucide-react';

const ViewAnnonceModal = ({ isOpen, onClose, annonce, onAction }) => {

    if (!isOpen || !annonce) return null;

    const handleAction = async (action) => {
        if (onAction) {
            await onAction(action, annonce.id);
            onClose();
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDownload = () => {
        console.log('Téléchargement PDF annonce:', annonce.id);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('fr-FR');
        } catch {
            return dateString;
        }
    };

    const getStatutColor = (statut) => {
        switch (statut?.toLowerCase()) {
            case 'brouillon':
            case 'en attente':
            case 'en-attente':
                return 'status-pending';
            case 'validée':
            case 'validee':
                return 'status-validated';
            case 'publiée':
            case 'publiee':
                return 'status-published';
            case 'rejetée':
            case 'rejetee':
                return 'status-rejected';
            default:
                return 'status-pending';
        }
    };

    // Extraire les informations depuis les données API
    const extractSocieteInfo = () => {
        if (annonce.nom_societe) return annonce.nom_societe;

        const content = annonce.contenu_complet || '';
        const matches = content.match(/dénommée\s*:\s*([^.\n]+)/i) ||
            content.match(/société\s*([^,\n]+)/i);

        return matches ? matches[1].trim() : 'N/A';
    };

    const extractFormeJuridique = () => {
        if (annonce.forme_juridique) return annonce.forme_juridique;
        if (annonce.immatriculation_details?.forme_juridique_libelle) {
            return annonce.immatriculation_details.forme_juridique_libelle;
        }

        const content = annonce.contenu_complet || '';
        const formes = [
            'Société Civile Immobilière',
            'Société à Responsabilité Limitée Unipersonnelle',
            'Société par Actions Simplifiée Unipersonnelle',
            'Société à Responsabilité Limitée',
            'SARL', 'SA', 'SAS', 'SASU', 'EURL', 'SCI', 'SNC'
        ];

        for (const forme of formes) {
            if (content.includes(forme)) {
                return forme;
            }
        }

        return 'N/A';
    };

    const extractSiegeSocial = () => {
        if (annonce.siege_social) return annonce.siege_social;

        const content = annonce.contenu_complet || '';
        const matches = content.match(/\*\*Siège\s*:\*\*\s*([^\n]+)/i) ||
            content.match(/Siège\s*:\s*([^\n]+)/i);

        return matches ? matches[1].trim() : 'N/A';
    };

    const extractCapital = () => {
        if (annonce.capital_social && annonce.capital_social !== "0.00") {
            return `${annonce.capital_social} ${annonce.devise || 'FCFA'}`;
        }

        const content = annonce.contenu_complet || '';
        const matches = content.match(/\*\*Capital\s*social\s*:\*\*\s*([^\n]+)/i) ||
            content.match(/Capital\s*:\s*([^\n]+)/i) ||
            content.match(/capital\s*de\s*([^\n,]+)/i);

        return matches ? matches[1].trim() : 'N/A';
    };

    const extractDuree = () => {
        if (annonce.duree) return annonce.duree;

        const content = annonce.contenu_complet || '';
        const matches = content.match(/\*\*Durée\s*:\*\*\s*([^\n]+)/i) ||
            content.match(/Durée\s*:\s*([^\n]+)/i);

        return matches ? matches[1].trim() : 'N/A';
    };

    const extractGerant = () => {
        if (annonce.gerant) return annonce.gerant;

        const content = annonce.contenu_complet || '';
        const matches = content.match(/\*\*Gérant\s*:\*\*\s*([^\n]+)/i) ||
            content.match(/Gérant\s*:\s*([^\n]+)/i) ||
            content.match(/gérant\s*:\s*([^,\n]+)/i);

        return matches ? matches[1].trim() : 'N/A';
    };

    const extractObjetSocial = () => {
        if (annonce.objet_social) return annonce.objet_social;

        const content = annonce.contenu_complet || '';
        const matches = content.match(/\*\*objet\*\*\s*:(.*?)(?=\*\*|Et pour la réalisation)/is) ||
            content.match(/Ayant pour objet\s*:(.*?)(?=Et pour la réalisation|Siège)/is);

        if (matches && matches[1]) {
            return matches[1].trim().replace(/\n/g, ' ').replace(/\s+/g, ' ');
        }

        return 'N/A';
    };

    const extractRCCM = () => {
        if (annonce.immatriculation_details?.reference) {
            return annonce.immatriculation_details.reference;
        }

        const content = annonce.contenu_complet || '';
        const matches = content.match(/CI-ABJ-[0-9-A-Z]+/i) ||
            content.match(/RCCM[^:]*:\s*([^\n.]+)/i);

        return matches ? matches[0].trim() : 'N/A';
    };

    const determineCategorie = () => {
        const content = annonce.contenu_complet || '';
        if (content.includes('MODIFICATION') || content.includes('modification')) return 'Modification';
        if (content.includes('RADIATION') || content.includes('radiation')) return 'Radiation';
        if (content.includes('constitué') || content.includes('CONSTITUTION')) return 'Immatriculation';
        return 'N/A';
    };

    const statutDisplay = annonce.statut === 'brouillon' ? 'En attente' : (annonce.statut || 'En attente');

    return (
        <div className="modal-overlay">
            <div className="modal-content annonce-preview">
                <div className="modal-header">
                    <div className="header-left">
                        <h3>Prévisualisation de l'annonce légale</h3>
                    </div>
                    <div className="header-right">
                        <button
                            className="action-button"
                            onClick={handlePrint}
                            title="Imprimer"
                        >
                            <Printer size={16} />
                        </button>
                        <button
                            className="action-button"
                            onClick={handleDownload}
                            title="Télécharger PDF"
                        >
                            <Download size={16} />
                        </button>
                        <button
                            className="modal-close"
                            onClick={onClose}
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="annonce-content">
                    {/* En-tête de l'annonce */}
                    <div className="annonce-header">
                        <h2>Annonce Légale</h2>
                        <span className={`status-badge ${getStatutColor(annonce.statut)}`}>
                            {statutDisplay}
                        </span>
                    </div>

                    {/* Type d'annonce */}
                    <div className="annonce-type">
                        <h3>
                            {determineCategorie() === 'Immatriculation' && 'AVIS DE CONSTITUTION'}
                            {determineCategorie() === 'Modification' && 'AVIS DE MODIFICATION'}
                            {determineCategorie() === 'Radiation' && 'AVIS DE RADIATION'}
                            {determineCategorie() === 'N/A' && 'AVIS LÉGAL'}
                        </h3>
                    </div>

                    {/* Contenu complet de l'annonce */}
                    {annonce.contenu_complet && (
                        <div className="annonce-full-content">
                            <div dangerouslySetInnerHTML={{
                                __html: annonce.contenu_complet
                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    .replace(/\n/g, '<br/>')
                                    .replace(/```markdown/g, '')
                                    .replace(/```/g, '')
                            }} />
                        </div>
                    )}

                    {/* Informations structurées (fallback si pas de contenu_complet) */}
                    {!annonce.contenu_complet && (
                        <>
                            {/* Informations de la société */}
                            <div className="company-info">
                                <div className="info-section">
                                    <span className="label">SOCIÉTÉ :</span>
                                    <span className="value">« {extractSocieteInfo()} »</span>
                                </div>

                                <div className="info-section">
                                    <span className="label">FORME JURIDIQUE :</span>
                                    <span className="value">{extractFormeJuridique()}</span>
                                </div>

                                <div className="info-section">
                                    <span className="label">SIÈGE SOCIAL :</span>
                                    <span className="value">{extractSiegeSocial()}</span>
                                </div>

                                <div className="info-section">
                                    <span className="label">CAPITAL :</span>
                                    <span className="value">{extractCapital()}</span>
                                </div>

                                <div className="info-section">
                                    <span className="label">DURÉE :</span>
                                    <span className="value">{extractDuree()}</span>
                                </div>
                            </div>

                            {/* Objet social */}
                            <div className="objet-section">
                                <span className="label">Ayant pour objet :</span>
                                <div className="objet-content">
                                    <p>{extractObjetSocial()}</p>
                                </div>
                            </div>

                            {/* Gérant */}
                            <div className="gerant-section">
                                <span className="label">POUR AVIS, LE GÉRANT</span>
                                <span className="value">{extractGerant()}</span>
                            </div>
                        </>
                    )}

                    {/* Informations techniques */}
                    <div className="technical-info">
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">ID:</span>
                                <span className="info-value">{annonce.id}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Référence:</span>
                                <span className="info-value">{extractRCCM()}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Date de création:</span>
                                <span className="info-value">
                                    {formatDate(annonce.created_at)}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Date de parution:</span>
                                <span className="info-value">
                                    {formatDate(annonce.date_publication)}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Catégorie:</span>
                                <span className="info-value">{determineCategorie()}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Tribunal:</span>
                                <span className="info-value">
                                    {annonce.immatriculation_details?.tribunal_nom ||
                                        annonce.tribunal_details?.nom || 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions selon le statut */}
                <div className="modal-actions">
                    <button
                        type="button"
                        className="cancel-button"
                        onClick={onClose}
                    >
                        Fermer
                    </button>

                    {annonce.statut === 'brouillon' && (
                        <>
                            <button
                                className="reject-button"
                                onClick={() => handleAction('rejeter')}
                            >
                                <XCircle size={16} />
                                Rejeter
                            </button>
                            <button
                                className="validate-button"
                                onClick={() => handleAction('valider')}
                            >
                                <CheckCircle size={16} />
                                Valider
                            </button>
                        </>
                    )}

                    {(annonce.statut === 'validee' || annonce.statut === 'validée') && (
                        <button
                            className="publish-button"
                            onClick={() => handleAction('publier')}
                        >
                            Publier
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewAnnonceModal;